import { ApiError } from '@/network/api-client';
import type { ForegroundSource, PresenceApi, PresenceClock } from '../types/presence.types';
type Episode = { id: string; sessionId?: string; phase: 'start' | 'waiting' | 'qualify' | 'done' | 'terminal'; attempts: number; exhausted: boolean };
export class CharacterPresenceController {
  private active = false;
  private generation = 0;
  private episode: Episode | null = null;
  private request: AbortController | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private unsubscribe: (() => void)[] = [];
  constructor(private readonly api: PresenceApi, private readonly source: ForegroundSource,
    private readonly clock: PresenceClock, private readonly newId: () => string = () => crypto.randomUUID()) {}
  start = () => {
    if (this.active) return;
    this.active = true;
    this.unsubscribe = [this.source.subscribeAction(this.action), this.source.subscribeVisibility(visible => {
      if (!visible) this.reset();
    })];
  };
  stop = () => {
    this.active = false;
    this.unsubscribe.forEach(remove => remove());
    this.unsubscribe = [];
    this.reset();
  };
  private reset() {
    this.generation++;
    this.request?.abort();
    this.request = null;
    if (this.timer !== null) this.clock.clearTimeout(this.timer);
    this.timer = null;
    this.episode = null;
  }
  private action = (trusted: boolean) => {
    if (!trusted || !this.active || !this.source.isVisible()) return;
    if (!this.episode) this.episode = { id: this.newId(), phase: 'start', attempts: 0, exhausted: false };
    else if (!this.episode.exhausted) return;
    this.episode.attempts = 0;
    this.episode.exhausted = false;
    void this.perform();
  };
  private schedule(callback: () => void, delay: number) {
    this.timer = this.clock.setTimeout(() => { this.timer = null; callback(); }, delay);
  }
  private async perform() {
    const episode = this.episode;
    if (!episode || !this.active || !this.source.isVisible() || this.request ||
      (episode.phase !== 'start' && episode.phase !== 'qualify')) return;
    const generation = this.generation;
    const request = new AbortController();
    this.request = request;
    episode.attempts++;
    const current = () => this.active && this.generation === generation && this.episode === episode && this.source.isVisible();
    try {
      if (episode.phase === 'start') {
        const reply = await this.api.start({ episodeId: episode.id }, request.signal);
        if (!current()) return;
        if (reply.episodeId !== episode.id || !reply.sessionId || reply.qualificationSeconds !== 30) {
          episode.phase = 'terminal'; return;
        }
        episode.sessionId = reply.sessionId;
        episode.phase = 'waiting';
        episode.attempts = 0;
        // Conservatively count continuous visibility after the server challenge arrives.
        this.schedule(() => { episode.phase = 'qualify'; void this.perform(); }, 30_000);
      } else {
        const reply = await this.api.qualify({ sessionId: episode.sessionId! }, request.signal);
        if (!current()) return;
        episode.phase = reply.sessionId === episode.sessionId && reply.status === 'qualified' ? 'done' : 'terminal';
      }
    } catch (error) {
      if (!current()) return;
      const transient = !(error instanceof ApiError) || [429, 503].includes(error.status) ||
        (error.status === 409 && /presence_(processing|too_early)/.test(error.body));
      if (!transient) episode.phase = 'terminal';
      else if (episode.attempts >= 3) episode.exhausted = true;
      else this.schedule(() => { void this.perform(); }, episode.attempts * 1000);
    } finally {
      if (this.request === request) this.request = null;
    }
  }
}
